import { useEffect, useMemo, useState } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import {
  filterSwarmByUnits,
  filterSwarmFeed,
  SWARM_FEED_ALL,
  swarmLocationOptions,
  swarmOperatingModelOptions,
  type SwarmFeedChart,
  type SwarmFeedOption,
  type SwarmFeedYearRange,
} from '../../functions/dataFilters/swarmFeed';
import type { PeriodMode } from '../../functions/calculations/unitFinancials';
import type { Risk } from '../../types/api';
import { dataService } from '../../functions/apis/dataService';
import { usePortfolioOverview } from './usePortfolioOverview';

type UseOverviewSwarmFeedInput = {
  chart: SwarmFeedChart;
  periodMode: PeriodMode;
  anchorDateKey: string | null;
  yearRange: SwarmFeedYearRange | null;
};

export function useOverviewSwarmFeed({
  chart,
  periodMode,
  anchorDateKey,
  yearRange,
}: UseOverviewSwarmFeedInput) {
  const [risks, setRisks] = useState<Risk[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [locationKey, setLocationKey] = useState(SWARM_FEED_ALL);
  const [operatingModelKey, setOperatingModelKey] = useState(SWARM_FEED_ALL);
  const { clientUnits, selectedClient } = useFilter();
  const overview = usePortfolioOverview();

  useEffect(() => {
    setRisks(overview.data?.risks.items || []);
    setNextCursor(overview.data?.risks.next_cursor || null);
  }, [overview.data]);

  const scopedRisks = useMemo(
    () => filterSwarmByUnits(risks, clientUnits),
    [risks, clientUnits],
  );

  const locationOptions = useMemo<SwarmFeedOption[]>(
    () => swarmLocationOptions(scopedRisks),
    [scopedRisks],
  );
  const operatingModelOptions = useMemo<SwarmFeedOption[]>(
    () => swarmOperatingModelOptions(scopedRisks),
    [scopedRisks],
  );

  useEffect(() => {
    if (locationKey !== SWARM_FEED_ALL && !locationOptions.some((option) => option.key === locationKey)) {
      setLocationKey(SWARM_FEED_ALL);
    }
  }, [locationKey, locationOptions]);

  useEffect(() => {
    if (
      operatingModelKey !== SWARM_FEED_ALL
      && !operatingModelOptions.some((option) => option.key === operatingModelKey)
    ) {
      setOperatingModelKey(SWARM_FEED_ALL);
    }
  }, [operatingModelKey, operatingModelOptions]);

  const records = useMemo(
    () =>
      filterSwarmFeed(scopedRisks, {
        chart,
        locationKey,
        operatingModelKey,
        periodMode,
        anchorDateKey,
        yearRange,
      }),
    [scopedRisks, chart, locationKey, operatingModelKey, periodMode, anchorDateKey, yearRange],
  );

  const loadMore = async () => {
    if (!nextCursor || !selectedClient?.user_id_no || loadingMore) return;

    setLoadingMore(true);
    try {
      const page = await dataService.getRiskPage({
        user_id_no: selectedClient.user_id_no,
        limit: 20,
        cursor: nextCursor,
      });
      setRisks((currentRisks) => [...currentRisks, ...page.items]);
      setNextCursor(page.next_cursor || null);
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    records,
    loading: overview.loading,
    error: Boolean(overview.error),
    loadingMore,
    hasMore: Boolean(nextCursor),
    locationKey,
    operatingModelKey,
    locationOptions,
    operatingModelOptions,
    setLocationKey,
    setOperatingModelKey,
    loadMore,
  };
}
